import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const LanguageContext = createContext();

// Languages currently visible to visitors. To enable French publicly,
// add 'fr' back to this list once all FR translations are complete.
const SUPPORTED_LANGUAGES = ['nl', 'en'];
const DEFAULT_LANGUAGE = 'nl';
// Visitors with an unsupported browser language get English as the universal fallback.
const FALLBACK_FOR_UNSUPPORTED = 'en';

/**
 * Pick the best initial language for a first-time visitor based on browser settings.
 * - Reads the browser's preferred languages (navigator.languages, falls back to navigator.language)
 * - Returns the first one that we support (matched on the 2-letter prefix, e.g. "en-GB" -> "en")
 * - Falls back to FALLBACK_FOR_UNSUPPORTED ('en') when none of the browser languages match a supported one.
 */
const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || ''];
  for (const candidate of candidates) {
    const prefix = (candidate || '').toLowerCase().split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(prefix)) return prefix;
  }
  return FALLBACK_FOR_UNSUPPORTED;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);

  useEffect(() => {
    // 1) User has previously chosen a language -> always honour their choice
    const savedLang = localStorage.getItem('language');
    if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
      setLanguage(savedLang);
      return;
    }
    // 2) First-time visitor -> auto-detect from browser language
    setLanguage(detectBrowserLanguage());
  }, []);

  const toggleLanguage = () => {
    // Cycle NL -> EN -> FR -> NL
    const order = ['nl', 'en', 'fr'];
    const idx = order.indexOf(language);
    const newLang = order[(idx + 1) % order.length];
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  /**
   * Explicit setter — used by the dropdown to jump directly to a language.
   */
  const changeLanguage = (lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  /**
   * Translate helper. Backward compatible:
   *   t('Hallo', 'Hello')              -> NL/EN, falls back to EN for FR
   *   t('Hallo', 'Hello', 'Bonjour')   -> NL/EN/FR
   */
  const t = (nl, en, fr) => {
    if (language === 'nl') return nl;
    if (language === 'fr') return fr ?? en;
    return en;
  };

  const value = useMemo(() => ({
    language,
    toggleLanguage,
    changeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    t
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};